/**
 * GroupedSourcePanel - Groups sources by document with expandable cards
 * Replaces flat source list in AgentSimple.tsx
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as ArticleIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { groupSourcesByDocument, GroupedSource } from '../../utils/sourceGrouping';

interface Source {
  id: string;
  content: string;
  similarity: number;
  document_id?: string;
  block_type?: string;
  ref_ids?: number[];
  metadata?: any;
  num?: number;
}

interface GroupedSourcePanelProps {
  sources: Source[];
  onClose: () => void;
  isMobile?: boolean;
}

const BLOCK_TYPE_COLORS: Record<string, string> = {
  table: '#ed6c02',
  figure: '#9c27b0',
  text: '#1976d2',
  list: '#2e7d32',
  listitem: '#2e7d32',
};

const getBlockTypeColor = (blockType?: string): string => {
  if (!blockType) return '#757575';
  return BLOCK_TYPE_COLORS[blockType.toLowerCase()] || '#757575';
};

const openDocumentViewer = (source: Source) => {
  const params = new URLSearchParams();
  if (source.document_id) params.set('id', source.document_id);
  const refs = source.ref_ids || source.metadata?.all_refs || source.metadata?.source_block_refs || [];
  if (refs.length > 0) {
    params.set('highlightRefs', refs.join(','));
  } else if (source.metadata?.ref_id != null) {
    params.set('highlightRef', source.metadata.ref_id.toString());
  } else if (source.metadata?.block_id) {
    params.set('highlightBlock', source.metadata.block_id);
  }
  window.open(`/document-pro?${params.toString()}`, '_blank');
};

const GroupedSourcePanel: React.FC<GroupedSourcePanelProps> = ({ sources, onClose, isMobile }) => {
  const groups = useMemo(() => groupSourcesByDocument(sources), [sources]);

  // First 2 docs expanded by default
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    groups.slice(0, 2).forEach(g => initial.add(g.document_id));
    return initial;
  });

  const toggleDoc = (docId: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Sources ({sources.length} blocks, {groups.length} docs)
        </Typography>
        <IconButton size="small" onClick={onClose}>
          ×
        </IconButton>
      </Box>

      {/* Grouped cards */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {groups.map((group: GroupedSource) => {
          const isExpanded = expandedDocs.has(group.document_id);
          return (
            <Box
              key={group.document_id}
              sx={{
                mb: 1.5,
                border: '1px solid #E0E0E0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {/* Document header */}
              <Box
                onClick={() => toggleDoc(group.document_id)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  bgcolor: isExpanded ? '#F5F9FF' : 'white',
                  '&:hover': { bgcolor: '#F0F4F8' },
                }}
              >
                <ArticleIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap title={group.title}>
                    {group.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {group.report_date && (
                      <Typography variant="caption" color="text.secondary">
                        {group.report_date}
                      </Typography>
                    )}
                    {group.analyst && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {group.analyst}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Chip
                  label={group.blocks.length}
                  size="small"
                  sx={{ bgcolor: '#E3F2FD', color: '#1976d2', fontWeight: 600, minWidth: 28, height: 22 }}
                />
                {isExpanded ? <ExpandLessIcon sx={{ color: '#999' }} /> : <ExpandMoreIcon sx={{ color: '#999' }} />}
              </Box>

              {/* Expanded blocks */}
              <Collapse in={isExpanded}>
                <Box sx={{ borderTop: '1px solid #E0E0E0' }}>
                  {group.blocks.map((source, idx) => (
                    <Box
                      key={source.id || idx}
                      onClick={() => openDocumentViewer(source)}
                      sx={{
                        p: 1.5,
                        borderBottom: idx < group.blocks.length - 1 ? '1px solid #f0f0f0' : 'none',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#FAFAFA' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {source.num && (
                          <Chip
                            label={`[${source.num}]`}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#E3F2FD', color: '#1976d2', fontWeight: 600 }}
                          />
                        )}
                        <Chip
                          label={source.block_type || 'text'}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            bgcolor: getBlockTypeColor(source.block_type) + '15',
                            color: getBlockTypeColor(source.block_type),
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        />
                        {source.metadata?.ref_id != null && (
                          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                            ref:{source.metadata.ref_id}
                          </Typography>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <OpenIcon sx={{ fontSize: 14, color: '#999' }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#555',
                          fontSize: '0.8rem',
                          display: '-webkit-box',
                          WebkitLineClamp: isMobile ? 2 : 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4,
                        }}
                      >
                        {source.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GroupedSourcePanel;
